import type { ReactElement } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  EXPOSURE_RISK_CATEGORIES,
  EXPOSURE_RISK_CRITICALITIES,
  EXPOSURE_RISK_TYPES,
  MAX_EXPOSURE_RISK_ASSUMPTIONS,
  widgetTargetKey,
  type ExposureRiskCandidate,
  type ExposureRiskCategory,
  type ExposureRiskCriticality,
  type ExposureRiskType,
} from "@playbookai/widget-schemas";
import { Button } from "../../atoms/Button/Button.js";
import { Input } from "../../atoms/Input/Input.js";
import { Textarea } from "../../atoms/Textarea/Textarea.js";
import { Select } from "../../molecules/Select/Select.js";
import { ActionMenu } from "../../molecules/ActionMenu/ActionMenu.js";
import { EmptyState } from "../../molecules/EmptyState/index.js";
import { useFieldEditSession } from "../field-edit-session.js";
import {
  EXPOSURE_RISK_CATEGORY_LABELS,
  EXPOSURE_RISK_CRITICALITY_LABELS,
  EXPOSURE_RISK_TYPE_LABELS,
} from "./presentation.js";

/**
 * Detail column of the expanded editor — the whole candidate on one surface.
 * Every field maps 1:1 to the schema; there is no derived score and no
 * workflow state here.
 */
export function CandidateRiskEditor({
  risk,
  readOnly,
  onChange,
  onDelete,
}: {
  readonly risk: ExposureRiskCandidate;
  readonly readOnly?: boolean | undefined;
  readonly onChange: (patch: Partial<ExposureRiskCandidate>) => void;
  readonly onDelete: () => void;
}): ReactElement {
  const disabled = readOnly === true;
  /**
   * PB-4923 AC5 — seven leaves plus one value set on the selected risk. A risk
   * is committed the moment it is added (`addRisk` calls `onChange`), so unlike
   * the draft-based row editors there is no unsaved candidate here and every
   * control may claim its target immediately.
   */
  const target = (field: string): string =>
    widgetTargetKey([
      { kind: "item", collection: "risks", itemId: risk.id },
      { kind: "prop", name: field },
    ]);
  const titleLease = useFieldEditSession(target("title"));
  const typeLease = useFieldEditSession(target("type"));
  const criticalityLease = useFieldEditSession(target("criticality"));
  const affectedScopeLease = useFieldEditSession(target("affectedScope"));
  const categoryLease = useFieldEditSession(target("category"));
  const descriptionLease = useFieldEditSession(target("description"));
  const basisLease = useFieldEditSession(target("basis"));
  /*
   * `assumptions` is a `MediumText[]` ITEM VALUE SET on the risk: one unit,
   * addressed as `risks[id]/assumptions`, because an assumption is a bare
   * string with no identity of its own and could otherwise only be reached by
   * array position. Every row claims that one key for exactly that reason.
   *
   * The claim now does two jobs rather than one. It still stops an AI
   * `remove_item` taking the risk out from under somebody mid-sentence —
   * `checkActiveEdit` matches a removal by DESCENDANT — and it is now also a
   * live edit session on a target a `set_field` CAN land on, so an AI proposal
   * defers to whoever is typing here instead of overwriting them.
   */
  const assumptionsLease = useFieldEditSession(target("assumptions"));
  const setAssumption = (index: number, value: string): void =>
    onChange({
      assumptions: risk.assumptions.map((assumption, current) =>
        current === index ? value : assumption,
      ),
    });

  return (
    <section className="flex min-h-0 min-w-0 flex-col rounded-lg border border-pb-border bg-pb-card-elevated/30">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        <header className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-pb-text-secondary">
            Selected risk
          </h3>
          {!disabled && (
            <ActionMenu
              triggerLabel="Risk options"
              triggerSize="sm"
              items={[
                {
                  id: "delete",
                  label: `Delete ${risk.title || "candidate risk"}`,
                  icon: Trash2,
                  tone: "critical",
                  onSelect: onDelete,
                },
              ]}
            />
          )}
        </header>
        <Input
          label="Candidate risk"
          value={risk.title}
          maxLength={255}
          disabled={disabled}
          {...titleLease}
          onChange={(event) => onChange({ title: event.target.value })}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="Type"
            value={risk.type}
            disabled={disabled}
            {...typeLease}
            options={EXPOSURE_RISK_TYPES.map((value) => ({
              value,
              label: EXPOSURE_RISK_TYPE_LABELS[value],
            }))}
            onChange={(value) => onChange({ type: value as ExposureRiskType })}
          />

          <Select
            label="Criticality"
            value={risk.criticality}
            disabled={disabled}
            {...criticalityLease}
            options={EXPOSURE_RISK_CRITICALITIES.map((value) => ({
              value,
              label: EXPOSURE_RISK_CRITICALITY_LABELS[value],
            }))}
            onChange={(value) =>
              onChange({ criticality: value as ExposureRiskCriticality })
            }
          />
        </div>

        <Input
          label="Affected scope"
          value={risk.affectedScope}
          maxLength={500}
          disabled={disabled}
          {...affectedScopeLease}
          onChange={(event) => onChange({ affectedScope: event.target.value })}
        />
        <details className="border-t border-pb-border">
          <summary className="cursor-pointer py-3 text-sm text-pb-text-secondary">
            Supporting details
          </summary>
          <div className="space-y-3 pb-3">
            <Select
              label="Category"
              value={risk.category}
              disabled={disabled}
              {...categoryLease}
              options={EXPOSURE_RISK_CATEGORIES.map((value) => ({
                value,
                label: EXPOSURE_RISK_CATEGORY_LABELS[value],
              }))}
              onChange={(value) =>
                onChange({ category: value as ExposureRiskCategory })
              }
            />
            <Textarea
              label="Description"
              value={risk.description}
              maxLength={500}
              rows={3}
              disabled={disabled}
              {...descriptionLease}
              onChange={(event) =>
                onChange({ description: event.target.value })
              }
            />
          </div>
        </details>
        <details className="border-t border-pb-border">
          <summary className="cursor-pointer py-3 text-sm text-pb-text-secondary">
            Evidence basis · {risk.basis.trim() ? "Recorded" : "Not recorded"}
          </summary>
          <div className="pb-3">
            <Input
              label="Evidence basis"
              value={risk.basis}
              maxLength={500}
              disabled={disabled}
              {...basisLease}
              onChange={(event) => onChange({ basis: event.target.value })}
            />
          </div>
        </details>
        <details className="border-t border-pb-border">
          <summary className="cursor-pointer py-3 text-sm text-pb-text-secondary">
            Assumptions ({risk.assumptions.length})
          </summary>
          <fieldset className="min-w-0 pb-3">
            <legend className="mb-1.5 block text-xs font-medium text-pb-text-secondary">
              Assumptions
            </legend>
            {risk.assumptions.length === 0 ? (
              <EmptyState size="sm" title="No assumptions recorded yet." />
            ) : (
              <ul className="space-y-2">
                {risk.assumptions.map((assumption, index) => (
                  <li
                    // Assumptions are positional free text with no id — the index
                    // is the identity the schema gives us.
                    key={index}
                    className="flex min-w-0 items-center gap-2"
                  >
                    <Input
                      aria-label={`Assumption ${index + 1}`}
                      value={assumption}
                      maxLength={500}
                      disabled={disabled}
                      {...assumptionsLease}
                      onChange={(event) =>
                        setAssumption(index, event.target.value)
                      }
                    />
                    <Button
                      iconOnly
                      size="sm"
                      variant="ghost"
                      aria-label={`Delete assumption ${index + 1}`}
                      disabled={disabled}
                      onClick={() =>
                        onChange({
                          assumptions: risk.assumptions.filter(
                            (_, current) => current !== index,
                          ),
                        })
                      }
                      className="shrink-0 rounded text-pb-text-muted transition-colors hover:bg-pb-red/10 hover:text-pb-red-text focus-ring"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              disabled={
                disabled ||
                risk.assumptions.length >= MAX_EXPOSURE_RISK_ASSUMPTIONS
              }
              onClick={() =>
                onChange({ assumptions: [...risk.assumptions, ""] })
              }
            >
              <Plus className="h-4 w-4" />
              Add assumption
            </Button>
          </fieldset>
        </details>
      </div>
    </section>
  );
}
